import { createAction, createReducer } from 'redux-act';
import Immutable from 'seamless-immutable';

import getRandom from '../util/getRandom';

const INITIAL_STATE = Immutable({
  tests: [],
  selectedTest: null,
  selectedTestId: null,
  isLoading: false,
  isLoadingResults: false,
  isLoadingQuestions: false,
  isLoadingExamTests: false,
  questions: [],
  questionsByTest: {},
  answersGiven: {},
  runStartedAt: null,
  correctAnswersCount: 0,
  completedDialogOpen: false,
  previewTests: [],
  results: [],
  testRun: null,
  testRuns: [],
  exams: [],
  currentQuestion: null,
});

export const selectTest = createAction('SELECTTEST');
export const selectResult = createAction('SELELTRESULT');

// api actions
export const onLoadTests = createAction('ONLOADTESTS');
export const onLoadTest = createAction('ONLOAD_TEST_QUESTIONS');
export const onLoadResults = createAction('ONLOADRESULTS');

export const onLoadAllTestQuestions = createAction('ONLOADALLTESTQUESTIONS');

// utils
export const onLoadTestsSucceeded = createAction('ONLOADTESTSSUCCEEDED');
export const onLoadTestsFailed = createAction('ONLOADTESTSFAILED');

export const onLoadTestSucceeded = createAction('ONLOAD_TEST_QUESTIONSSUCCEEDED');
export const onLoadTestFailed = createAction('ONLOAD_TEST_QUESTIONSFAILED');

export const onSubmitAnswer = createAction('ON_SUBMIT_ANSWER_ACTION');
export const onSubmitTest = createAction('ON_SUBMIT_TEST_ACTION');
export const onSubmitTestSucceeded = createAction('ON_SUBMIT_TEST_SUCCEEDED_ACTION');

export const onLoadResultsSucceeded = createAction('ONLOAD_RESULTS_SUCCEEDED');
export const onRestart = createAction('restartCourseAction');

export const onCloseCompletedDialog = createAction('completedDialogOpenAction');

export const onStartTest = createAction('ON_START_TEST');
export const onStartTestSucceeded = createAction('ON_START_TEST_SUCCESS');

export const onSetActiveTestRun = createAction('ON_SET_TEST_RUN');
export const onRemoveTestRun = createAction('ON_REMOVE_TEST_RUN');

export const onLoadExams = createAction('ON_LOAD_EXAMS');
export const onLoadExamsSucceeded = createAction('ON_LOAD_EXAMS_SUCCESS');
export const onLoadExamsFaild = createAction('ON_LOAD_EXAMS_FAILED');

export const selectExam = createAction('SELECT_EXAM');
export const onSubmitExamAnswer = createAction('ON_SUBMIT_EXAM_ANSWER');

export const loadSingleQuestion = createAction('LOADSINGLEQUESTION');
export const loadSingleQuestionSucceeded = createAction('LOADSINGLEQUESTIONSUCCESS');
export const loadSingleQuestionFailed = createAction('LOADSINGLEQUESTIONFAILED');

const restart = state => state.merge({
  answersGiven: {},
  correctAnswersCount: 0,
  runStartedAt: new Date().toISOString(),
  completedDialogOpen: false,
});


const findTest = (tests, id) => {
  for (let i = tests.length - 1; i >= 0; i--) {
    const test = tests[i];

    if ((test.type == 'test' || test.type == null) && test.id == id) {
      return test;
    }

    if (test.type == 'group' && test.children) {
      for (let j = test.children.length - 1; j >= 0; j--) {
        if (test.children[j] && test.children[j].type == 'test' && test.children[j].id == id) {
          return test.children[j];
        }
      }
    }
  }
};


const submitAnswer = (state, { question, answersTicked }) => {
  const correctAnswers = question.answers.filter(a => a.isValidAnswer);
  const tickedAnswerIds = answersTicked ? Object.keys(answersTicked) : [];

  const correctNumber = tickedAnswerIds.length == correctAnswers.length;

  const isCorrect = correctNumber && correctAnswers.every(a => answersTicked[a.id]);

  const answer = {
    tickedAnswerIds,
    isCorrect,
  };

  const correctAnswersCount = isCorrect ? (state.correctAnswersCount + 1) : state.correctAnswersCount;

  const answersGiven = state.answersGiven.set(question.id, answer);
  return state.merge({
    answersGiven,
    correctAnswersCount,
  });
};

const loadQuestions = (state, { test, questions, result }) => {
  const questionsByTest = state.questionsByTest.setIn([test.id], questions);

  const selectedTest = result ? findTest(state.results, test.id) : findTest(state.tests, test.id) || test;
  return state.merge({
    questionsByTest,
    questions,
    runStartedAt: new Date().toISOString(),
    correctAnswersCount: 0,
    selectedTest,
    previewTests: getRandom(state.tests.filter(t => t.id != test.id), Math.min(4, state.tests.length - 1)),
    selectedTestId: test.id * 1,
    isLoadingQuestions: false,
  });
};

const selectTestReducer = (state, test) => {
  const selectedTest = findTest(state.tests, test.id);
  const testRun = state.testRuns.find(t => t.testId === test.id);

  return state.merge({
    selectedTest,
    selectedTestId: test.id * 1,
    isLoadingQuestions: true,
    testRun,
  });
};

const selectExamReducer = (state, test) => {
  const selectedExam = state.exams.find(e => e.test.id == test.id);

  if (!selectedExam) return state;

  const testRun = state.testRuns.find(r => r.testId == selectedExam.test.id);

  return state.merge({
    selectedExam,
    selectedTest: selectedExam.test,
    selectedTestId: selectedExam.test.id * 1,
    isLoadingQuestions: true,
    testRun,
  });
};

const selectResultReducer = (state, test) => {
  const selectedTest = findTest(state.results, test.id);

  return state.merge({
    selectedTest,
    selectedTestId: test.id * 1,
    isLoadingQuestions: true,
  });
};

const startTestSuccess = (state, testRun) => {
  const testRuns = state.testRuns.asMutable();
  testRuns.push(testRun);
  return state.merge({
    selectedExam: null,
    testRuns,
    testRun,
  });
};

const removeTestRun = (state, testRunId) => state.merge({
  testRuns: state.testRuns.filter(r => r.id !== testRunId),
});


const reducers = createReducer({
  [onLoadTests]: state => state.merge({ isLoading: true }),
  [onLoadResults]: state => state.merge({ isLoadingResults: true }),
  [selectTest]: selectTestReducer,
  [selectExam]: selectExamReducer,
  [selectResult]: selectResultReducer,
  [onRemoveTestRun]: removeTestRun,
  [onLoadTestsSucceeded]: (state, { tests }) => state.merge({
    tests,
    previewTests: getRandom(tests, Math.min(4, tests.length - 1)),
    isLoading: false,
  }),
  [onLoadResultsSucceeded]: (state, { results }) => {
    if (!results || !results.reverse) {
      return state.merge({
        results: [],
        isLoadingResults: false,
      });
    }
    return state.merge({
      results: results.filter(f => f.endDate).reverse(),
      testRuns: results.filter(f => !f.endDate).reverse(),
      isLoadingResults: false,
    });
  },
  [onSubmitTest]: state => state.merge({ completedDialogOpen: true }),
  [onLoadTestSucceeded]: loadQuestions,
  [onStartTestSucceeded]: startTestSuccess,
  [onSubmitAnswer]: submitAnswer,
  [onRestart]: restart,
  // [onSubmitTest]: state => state.merge({ completedDialogOpen: true }),
  [onCloseCompletedDialog]: state => state.merge({ completedDialogOpen: false }),
  [onSetActiveTestRun]: (state, testRun) => state.merge(testRun),
  [onLoadExams]: state => state.merge({ isLoadingExamTests: true }),
  [onLoadExamsSucceeded]: (state, exams) => state.merge({ exams, isLoadingExamTests: false }),
  [onLoadExamsFaild]: state => state.merge({ isLoadingExamTests: false }),
  [onLoadAllTestQuestions]: state => state.merge({ isLoadingQuestions: true }),
  [loadSingleQuestionSucceeded]: (state, currentQuestion) => state.merge({ currentQuestion }),
}, INITIAL_STATE);

export default reducers;




// WEBPACK FOOTER //
// ./src/app/reducers/test.reducers.js